import type {
  ProductAdGuestType,
  ProductAdMode,
  ProductAdQuantityMode,
  SimpleProductAd,
  WbeReservationRow,
} from "../api/contracts/reservation";

export const DEFAULT_GUEST_TYPES: ProductAdGuestType[] = [
  "adult",
  "teenager",
  "child",
  "infant",
];

export const MANUAL_QUANTITY_MODES: ProductAdQuantityMode[] = [
  "manual_quantity",
  "manual_quantity_per_stay_date",
  "manual_quantity_per_guest_type_per_stay_date",
];

// The modes whose quantity the guest sets per stay date rather than as one count.
export const PER_STAY_DATE_MODES: ProductAdQuantityMode[] = [
  "manual_quantity_per_stay_date",
  "manual_quantity_per_guest_type_per_stay_date",
];

const DELIVERY_TIMING_MODES: ProductAdQuantityMode[] = [
  "night",
  "person_night",
  "guest_type_night",
  "manual_quantity_per_stay_date",
];

export const isManualQuantityMode = (mode: ProductAdQuantityMode): boolean =>
  MANUAL_QUANTITY_MODES.includes(mode);

export const isPerStayDateMode = (mode: ProductAdQuantityMode): boolean =>
  PER_STAY_DATE_MODES.includes(mode);

export const supportsDeliveryTiming = (mode: ProductAdQuantityMode): boolean =>
  DELIVERY_TIMING_MODES.includes(mode);

/**
 * Maps a quantity mode to the i18n key used to label the price unit.
 * `guest_type`/`guest_type_night` are intentionally rendered as `person`/`person_night`
 * to avoid leaking PMS-internal terminology into the booking flow.
 * The `legacyMode` is used only when the quantity mode is unknown (e.g. ad data
 * created before quantityMode existed).
 */
export const getDisplayModeKey = (
  quantityMode: ProductAdQuantityMode,
  legacyMode: ProductAdMode,
): string => {
  switch (quantityMode) {
    case "guest_type":
      return "person";
    case "guest_type_night":
      return "person_night";
    case "person":
    case "night":
    case "stay":
    case "person_night":
      return quantityMode;
    default:
      return legacyMode;
  }
};

const GUEST_TYPE_LABEL_KEY: Record<ProductAdGuestType, string> = {
  adult: "guest_types.adults_one",
  teenager: "guest_types.teenagers_one",
  child: "guest_types.children_one",
  infant: "guest_types.infants_one",
};

export const getGuestTypeLabelKey = (guestType: ProductAdGuestType): string =>
  GUEST_TYPE_LABEL_KEY[guestType];

const guestCountForType = (
  numberOfGuests: WbeReservationRow["numberOfGuests"],
  guestType: ProductAdGuestType,
): number => {
  switch (guestType) {
    case "adult":
      return numberOfGuests.adults;
    case "teenager":
      return numberOfGuests.teenagers;
    case "child":
      return numberOfGuests.children;
    case "infant":
      return numberOfGuests.infants;
  }
};

/**
 * Hides ads restricted to guest types absent from the booking. An ad with no
 * `guestTypes` is treated as applying to every guest type and is always kept.
 */
export const filterProductAdsForGuestTypes = (
  productAds: SimpleProductAd[],
  numberOfGuests: WbeReservationRow["numberOfGuests"],
): SimpleProductAd[] =>
  productAds.filter((productAd) => {
    if (!productAd.guestTypes?.length) return true;
    return productAd.guestTypes.some(
      (guestType) => guestCountForType(numberOfGuests, guestType) > 0,
    );
  });

/**
 * Booked guests of the given types. A per-guest-type-per-stay-date ad steps by
 * this, so each click adds one item per guest the ad applies to. An ad with no
 * `guestTypes` applies to every guest.
 */
export const countGuestsOfTypes = (
  guestTypes: ProductAdGuestType[] | undefined,
  numberOfGuests: WbeReservationRow["numberOfGuests"],
): number =>
  (guestTypes?.length ? guestTypes : DEFAULT_GUEST_TYPES).reduce(
    (sum, guestType) => sum + guestCountForType(numberOfGuests, guestType),
    0,
  );
